import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';

const API_BASE = 'https://api.wiseclean.ee/api:56Z3lZ-0';
const UPDATE_ENDPOINT = `${API_BASE}/update_old_order_options`;
const SUPPORT_EMAIL = 'info@wiseclean.eu';

function OrderOptions() {
    const { lang } = useParams();
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('orderID');
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const activeLang = ['en', 'et'].includes(lang) ? lang : 'en';

    const [step, setStep] = useState('checking');
    const [data, setData] = useState(null);
    const formRef = useRef(null);

    useEffect(() => {
        i18n.changeLanguage(activeLang);
    }, [activeLang, i18n]);

    useEffect(() => {
        if (!orderId) {
            setStep('error');
            return undefined;
        }
        let cancelled = false;
        fetch(`${API_BASE}/get_old_order_details?orderID=${encodeURIComponent(orderId)}&locale=${activeLang}`)
            .then((r) => {
                if (!r.ok) throw new Error(`get_old_order_details ${r.status}`);
                return r.json();
            })
            .then((d) => {
                if (!cancelled) {
                    setData(d);
                    setStep('ready');
                }
            })
            .catch(() => {
                if (!cancelled) setStep('error');
            });
        return () => {
            cancelled = true;
        };
    }, [orderId, activeLang]);

    const handleSubmit = (e) => {
        const form = formRef.current;
        if (!form) {
            e.preventDefault();
            return;
        }
        const checked = form.querySelectorAll('input[type="checkbox"]:checked');
        if (checked.length === 0) {
            e.preventDefault();
            alert(t('orderOptionsValidationError'));
            return;
        }
        // eslint-disable-next-line no-alert
        if (!window.confirm(t('orderOptionsConfirm'))) {
            e.preventDefault();
        }
        // Otherwise let the native browser form-submit fire (POST to Xano).
    };

    const handlePayOutstanding = () => {
        navigate(`/external/payment/${activeLang}/${orderId}`);
    };

    if (step === 'error') {
        return (
            <div style={styles.page}>
                <header style={styles.header}>
                    <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
                </header>
                <main style={styles.errorMain}>
                    <div style={styles.card}>
                        <h1 style={styles.errorTitle}>{t('loadFailedTitle')}</h1>
                        <p style={styles.errorBody}>
                            {orderId ? t('loadFailedBody') : t('invalidLinkBody')}
                        </p>
                        <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.supportLink}>
                            {t('contactSupport')} · {SUPPORT_EMAIL}
                        </a>
                    </div>
                </main>
            </div>
        );
    }

    if (step === 'checking' || !data) {
        return (
            <div style={styles.page}>
                <header style={styles.header}>
                    <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
                </header>
                <main style={styles.main}>
                    <div style={styles.loadingText}>{t('orderOptionsLoadingDetails')}</div>
                </main>
            </div>
        );
    }

    const selectedOptionIDs = Array.isArray(data.optionIDS) ? data.optionIDS : [];
    const availableOptions = (Array.isArray(data.AllOptions) ? data.AllOptions : [])
        .filter((opt) => opt && opt.recID && !selectedOptionIDs.includes(opt.recID));

    return (
        <div style={styles.page}>
            <style>
                {`
                .order-options-list > div {
                    display: flex;
                    align-items: center;
                    padding: 10px 0;
                    border-bottom: 1px solid var(--wc-border);
                }
                .order-options-list > div:last-child {
                    border-bottom: none;
                }
                .order-options-list input[type="checkbox"] {
                    width: 18px;
                    height: 18px;
                    accent-color: var(--wc-brand);
                    cursor: pointer;
                    margin-right: 12px;
                    flex-shrink: 0;
                }
                .order-options-list label {
                    flex: 1;
                    font-size: 14px;
                    color: var(--wc-text-primary);
                    cursor: pointer;
                    line-height: 1.4;
                }
                `}
            </style>
            <header style={styles.header}>
                <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
            </header>
            <main style={styles.main}>
                <div style={styles.card}>
                    <h1 style={styles.title}>{t('orderOptionsTitle')}</h1>

                    <p style={styles.summary}>
                        {t('orderOptionsSummary', { date: data.date, time: data.time })}
                    </p>

                    <div style={styles.detailsBlock}>
                        {data.address && (
                            <p style={styles.detailLine}>
                                <strong>{t('orderOptionsAddressLabel')}</strong> {data.address}
                            </p>
                        )}
                        {(data.rooms || data.bathrooms) && (
                            <p style={styles.detailLine}>
                                <strong>{t('orderOptionsPropertyLabel')}</strong>{' '}
                                {[data.rooms, data.bathrooms].filter(Boolean).join(', ')}
                            </p>
                        )}
                        {data.subscription && (
                            <p style={styles.detailLine}>
                                <strong>{t('orderOptionsScheduleLabel')}</strong> {data.subscription}
                            </p>
                        )}
                        {data.options && data.options.trim() && (
                            <p style={styles.detailLine}>
                                <strong>{t('orderOptionsCurrentOptionsLabel')}</strong> {data.options}
                            </p>
                        )}
                        {data.windows && (
                            <p style={styles.detailLine}>
                                <strong>{t('orderOptionsCurrentWindowsLabel')}</strong> {data.windows}
                            </p>
                        )}
                        {(data.price !== undefined && data.price !== null) && (
                            <p style={styles.detailLinePrice}>
                                {t('orderOptionsPriceLabel', { price: Number(data.price).toFixed(2) })}
                            </p>
                        )}
                    </div>

                    <h2 style={styles.subTitle}>{t('orderOptionsAddOptionsLabel')}</h2>

                    <form
                        ref={formRef}
                        action={UPDATE_ENDPOINT}
                        method="POST"
                        onSubmit={handleSubmit}
                        style={styles.optionsForm}
                    >
                        <input type="hidden" name="orderID" value={orderId} />
                        <input type="hidden" name="locale" value={activeLang} />
                        <div className="order-options-list">
                            {availableOptions.map((opt) => (
                                <div key={opt.recID}>
                                    <input
                                        type="checkbox"
                                        id={opt.recID}
                                        name="options[]"
                                        value={opt.recID}
                                    />
                                    <label htmlFor={opt.recID}>{opt.NameSite_en}</label>
                                </div>
                            ))}
                        </div>
                        <button type="submit" style={styles.primaryButton}>
                            {t('orderOptionsSubmitButton')}
                        </button>
                    </form>

                    <button
                        type="button"
                        style={styles.secondaryButton}
                        onClick={handlePayOutstanding}
                    >
                        {t('orderOptionsPayOutstanding')}
                    </button>
                </div>
            </main>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'var(--wc-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'center',
    },
    logo: {
        height: '36px',
        width: 'auto',
        objectFit: 'contain',
    },
    main: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '8px 20px 40px',
    },
    errorMain: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
    },
    loadingText: {
        color: 'var(--wc-text-secondary)',
        fontSize: '15px',
        padding: '40px 20px',
    },
    card: {
        width: '100%',
        maxWidth: '520px',
        background: 'var(--wc-bg-primary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-card)',
        boxShadow: 'var(--wc-shadow-card)',
        padding: '28px 24px',
        boxSizing: 'border-box',
    },
    title: {
        fontSize: '22px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
        margin: '0 0 8px',
    },
    summary: {
        margin: '0 0 16px',
        fontSize: '14px',
        color: 'var(--wc-text-secondary)',
    },
    detailsBlock: {
        background: 'var(--wc-bg-secondary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        padding: '14px',
        marginBottom: '20px',
    },
    detailLine: {
        margin: '4px 0',
        fontSize: '13px',
        color: 'var(--wc-text-secondary)',
        lineHeight: 1.45,
    },
    detailLinePrice: {
        margin: '10px 0 0',
        paddingTop: '10px',
        borderTop: '1px solid var(--wc-border)',
        fontSize: '15px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
    subTitle: {
        fontSize: '16px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
        margin: '0 0 8px',
    },
    optionsForm: {
        margin: '0 0 12px',
    },
    primaryButton: {
        display: 'block',
        width: '100%',
        background: 'var(--wc-brand)',
        color: 'var(--wc-on-brand)',
        border: 'none',
        borderRadius: 'var(--wc-radius-btn)',
        padding: '14px 16px',
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        boxShadow: 'var(--wc-shadow-cta)',
        marginTop: '16px',
    },
    secondaryButton: {
        display: 'block',
        width: '100%',
        background: 'transparent',
        color: 'var(--wc-brand-dark)',
        border: '1px solid var(--wc-brand)',
        borderRadius: 'var(--wc-radius-btn)',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        marginTop: '12px',
    },
    errorTitle: {
        fontSize: '20px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
        margin: '0 0 12px',
    },
    errorBody: {
        fontSize: '15px',
        color: 'var(--wc-text-secondary)',
        lineHeight: 1.5,
        margin: '0 0 16px',
    },
    supportLink: {
        color: 'var(--wc-brand-dark)',
        fontWeight: 600,
        textDecoration: 'none',
        fontSize: '15px',
    },
};

export default OrderOptions;
